/**
 * Live tournament countdown — start or end based on phase.
 * Weekly `repeat` windows are resolved client-side so "next start"
 * never shows as "until completion" after a cycle ends.
 */

const IST_TZ = 'Europe/Istanbul';
const WEEKDAY_MAP = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

function istanbulParts(date = new Date()) {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: IST_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return Object.fromEntries(
    fmt.formatToParts(date)
      .filter((p) => p.type !== 'literal')
      .map((p) => [p.type, p.value]),
  );
}

function parseMs(raw) {
  if (!raw) return null;
  const ms = new Date(raw).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function getRepeatConfig(promo) {
  const rpt = promo?.repeat || promo?.extra?.repeat || promo?.extra_json?.repeat;
  if (!rpt || typeof rpt !== 'object') return null;
  if (String(rpt.every || '').toLowerCase() !== 'week') return null;
  return rpt;
}

function nextWeeklyWindow(fromDate = new Date(), cfg = {}) {
  const startWeekday = Number(cfg.startWeekday ?? 1);
  const startHour = Number(cfg.startHour ?? 11);
  const startMinute = Number(cfg.startMinute ?? 0);
  const durationDays = Number(cfg.durationDays ?? 7);
  const cursor = new Date(fromDate.getTime());

  for (let i = 0; i < 14; i += 1) {
    const d = new Date(cursor.getTime() + i * 86400000);
    const parts = istanbulParts(d);
    const wd = WEEKDAY_MAP[parts.weekday] || 1;
    if (wd !== startWeekday) continue;
    const isoLocal = `${parts.year}-${parts.month}-${parts.day}T${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}:00+03:00`;
    const start = new Date(isoLocal);
    if (start.getTime() <= fromDate.getTime() - 1000) continue;
    const endParts = istanbulParts(new Date(start.getTime() + (durationDays - 1) * 86400000));
    const endsAt = new Date(`${endParts.year}-${endParts.month}-${endParts.day}T23:59:00+03:00`);
    return { startsAt: start, endsAt };
  }

  const startsAt = new Date(fromDate.getTime() + 86400000);
  const endsAt = new Date(startsAt.getTime() + durationDays * 86400000);
  return { startsAt, endsAt };
}

function currentOrNextWeeklyWindow(now = new Date(), cfg = {}) {
  const startWeekday = Number(cfg.startWeekday ?? 1);
  const startHour = Number(cfg.startHour ?? 11);
  const startMinute = Number(cfg.startMinute ?? 0);
  const durationDays = Number(cfg.durationDays ?? 7);

  for (let i = 0; i < 8; i += 1) {
    const d = new Date(now.getTime() - i * 86400000);
    const parts = istanbulParts(d);
    const wd = WEEKDAY_MAP[parts.weekday] || 1;
    if (wd !== startWeekday) continue;
    const isoLocal = `${parts.year}-${parts.month}-${parts.day}T${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}:00+03:00`;
    const startsAt = new Date(isoLocal);
    const endParts = istanbulParts(new Date(startsAt.getTime() + (durationDays - 1) * 86400000));
    const endsAt = new Date(`${endParts.year}-${endParts.month}-${endParts.day}T23:59:00+03:00`);
    if (now.getTime() <= endsAt.getTime() + 60_000) {
      return { startsAt, endsAt };
    }
  }
  return nextWeeklyWindow(now, cfg);
}

/** Effective start/end for display + phase (honours weekly repeat). */
export function resolveTournamentWindow(promo, now = Date.now()) {
  const rpt = getRepeatConfig(promo);
  const rawStart = parseMs(promo?.startsAt || promo?.starts_at);
  const rawEnd = parseMs(promo?.endsAt || promo?.ends_at);

  if (rpt) {
    const win = currentOrNextWeeklyWindow(new Date(now), rpt);
    return {
      startMs: win.startsAt.getTime(),
      endMs: win.endsAt.getTime(),
    };
  }

  return { startMs: rawStart, endMs: rawEnd };
}

export function getTournamentStartMs(promo, now = Date.now()) {
  return resolveTournamentWindow(promo, now).startMs;
}

export function getTournamentEndMs(promo, now = Date.now()) {
  return resolveTournamentWindow(promo, now).endMs;
}

/** @returns {'upcoming'|'active'|'ended'} */
export function getTournamentPhase(promo, now = Date.now()) {
  const { startMs: start, endMs: end } = resolveTournamentWindow(promo, now);
  if (start != null && now < start) return 'upcoming';
  if (end != null && now > end) return 'ended';
  return 'active';
}

function formatDiff(diff, locale = 'tr') {
  if (diff <= 0) return locale === 'en' ? '0:00' : '0:00';
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  if (days >= 1) {
    if (locale === 'en') return `${days} ${days === 1 ? 'day' : 'days'}`;
    return `${days} gün`;
  }
  if (hours >= 1 || mins >= 10) {
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

export function formatTournamentCountdown(promo, now = Date.now(), locale = 'tr') {
  const phase = getTournamentPhase(promo, now);
  if (phase === 'upcoming') {
    const start = getTournamentStartMs(promo, now);
    if (start == null) return '';
    return formatDiff(start - now, locale);
  }
  if (phase === 'ended') {
    return locale === 'en' ? 'Ended' : 'Bitti';
  }
  const end = getTournamentEndMs(promo, now);
  if (end != null) {
    const diff = end - now;
    if (diff <= 0) return locale === 'en' ? 'Ended' : 'Bitti';
    return formatDiff(diff, locale);
  }
  const staticLeft = String(promo?.daysLeft || promo?.timer || '').trim();
  return staticLeft || '';
}

export function parseLeaderboardBonus(row) {
  const fsDirect = String(row?.bonusFs || row?.fs || '').trim();
  const cashDirect = String(row?.bonusCash || '').trim();
  if (cashDirect || fsDirect) {
    return { cash: cashDirect || String(row?.bonus || '—'), fs: fsDirect };
  }
  const raw = String(row?.bonus || '').trim();
  if (!raw || raw === '—') return { cash: '—', fs: '' };
  const lines = raw.split(/\n|\||;/).map((s) => s.trim()).filter(Boolean);
  if (lines.length >= 2) {
    const fsLine = lines.find((l) => /fs/i.test(l)) || lines[1];
    const cashLine = lines.find((l) => l !== fsLine) || lines[0];
    return { cash: cashLine, fs: fsLine };
  }
  const fsMatch = raw.match(/(\+?\s*\d[\d\s.,]*\s*FS)\s*$/i);
  if (fsMatch) {
    return {
      cash: raw.replace(fsMatch[0], '').trim() || '—',
      fs: fsMatch[1].trim(),
    };
  }
  return { cash: raw, fs: '' };
}
